import { useState, useEffect, useMemo } from 'react';
import {
  FlaskConical, Plus, Play, Pause, Square, Trash2, AlertTriangle,
  Loader2, RefreshCw, X, Search, TrendingUp, TrendingDown, Activity,
} from 'lucide-react';
import FeatureGate from '../components/FeatureGate';
import { useActivePolling } from '../hooks/useActivePolling';
import {
  getPaperSessions, getPaperSession, createPaperSession,
  pausePaperSession, resumePaperSession, stopPaperSession, deletePaperSession,
  listStrategies, getExchangeConnections, getExchangeMarkets,
} from '../services/api';
import { InfoTip } from '../components/ui';

const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BTC/USDC', 'ETH/USDC', 'SOL/USDC'];
const MAX_RUNNING = 3;

function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    return (POPULAR_QUOTES.indexOf(quote) === -1 ? 100 : POPULAR_QUOTES.indexOf(quote)) * 1000
      + (POPULAR_BASES.indexOf(base) === -1 ? 999 : POPULAR_BASES.indexOf(base));
  };
  return [...pairs].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

function fmtUSD(n, dp = 2) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
}
function fmtPct(n, dp = 2) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—';
  return `${n >= 0 ? '+' : ''}${Number(n).toFixed(dp)}%`;
}

const STATUS_STYLE = {
  running:  { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'RUNNING' },
  paused:   { bg: 'bg-amber-500/15',   text: 'text-amber-400',   label: 'PAUSED'  },
  stopped:  { bg: 'bg-gray-500/15',    text: 'text-gray-400',    label: 'STOPPED' },
};

export default function PaperTradingPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [savedStrategies, setSavedStrategies] = useState([]);
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [toast, setToast] = useState(null);

  const runningCount = sessions.filter(s => s.status === 'running').length;
  const canCreate = runningCount < MAX_RUNNING && savedStrategies.length > 0 && connectedExchanges.length > 0;

  async function load(showSpinner = true) {
    if (showSpinner) setLoading(true);
    try {
      const [sess, strat, ex] = await Promise.all([
        getPaperSessions().then(r => r?.sessions || r?.data?.sessions || []).catch(() => []),
        listStrategies().then(r => r?.strategies || r?.data?.strategies || []).catch(() => []),
        getExchangeConnections().then(r => (r?.exchanges || r?.data?.exchanges || []).filter(e => e.connected)).catch(() => []),
      ]);
      setSessions(sess);
      setSavedStrategies(strat);
      setConnectedExchanges(ex);
    } catch (err) {
      if (showSpinner) setToast({ type: 'error', message: err.message });
    }
    if (showSpinner) setLoading(false);
  }

  useEffect(() => { load(); }, []);

  // Auto-poll every 30s when not in a modal — keeps live equity / trade count fresh
  const pollInterval = useActivePolling(30000, !showCreate);
  useEffect(() => {
    if (!pollInterval) return;
    const id = setInterval(() => load(false), pollInterval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollInterval]);

  async function action(fn, sessionId, successMsg, errorMsg = 'Action failed') {
    try {
      await fn(sessionId);
      setToast({ type: 'success', message: successMsg });
      await load(false);
    } catch (err) {
      setToast({ type: 'error', message: err.message || errorMsg });
    }
  }

  return (
    <FeatureGate feature="paperTrading">
      <div className="space-y-5 animate-fade-in max-w-7xl">
        <header>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <FlaskConical className="w-7 h-7 text-amber-400" />
              <h1 className="text-2xl font-bold text-white">Paper Trading</h1>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                Live simulation
                <InfoTip side="bottom">Real exchange candles, virtual capital. Worker evaluates each session every 5 min. Fills at candle close + 10 bps slippage + 40 bps fee. Sessions persist server-side.</InfoTip>
              </span>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              disabled={!canCreate}
              className="px-4 py-2 rounded-xl text-sm font-bold bg-amber-500 text-dark-900 hover:bg-amber-400 disabled:bg-dark-700 disabled:text-gray-500 disabled:cursor-not-allowed flex items-center gap-2">
              <Plus className="w-4 h-4" /> New session
            </button>
          </div>
          <p className="text-sm text-gray-400">
            Run a saved strategy against <span className="text-emerald-400">live market candles</span> with simulated execution.
            Validate before risking real capital — no orders fire, but P&L tracks as if they did.
            <span className="ml-2 text-gray-500">({runningCount}/{MAX_RUNNING} sessions running)</span>
          </p>
        </header>

        {/* Helpful banner if user can't create */}
        {!loading && savedStrategies.length === 0 && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="text-amber-300 font-semibold">No saved strategies yet</p>
              <p className="text-amber-300/80 mt-0.5">
                Paper Trading runs YOUR saved strategies (not generic presets).{' '}
                <a href="/strategy-builder" className="underline">Build one in Strategy Builder</a> first, save it, then come back.
              </p>
            </div>
          </div>
        )}
        {!loading && connectedExchanges.length === 0 && savedStrategies.length > 0 && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="text-amber-300 font-semibold">Connect an exchange</p>
              <p className="text-amber-300/80 mt-0.5">
                We need an exchange connection to fetch real price candles.{' '}
                <a href="/settings?tab=connections" className="underline">Connect one in Settings</a>. No real orders fire — just price data.
              </p>
            </div>
          </div>
        )}

        {/* Sessions list */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>
        ) : sessions.length === 0 ? (
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-12 text-center">
            <FlaskConical className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">No paper trading sessions yet</p>
            <p className="text-sm text-gray-500">
              Start a session to test a saved strategy against live market data without risking real money.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sessions.map(s => <SessionCard key={s.sessionId} s={s} onAction={action} />)}
          </div>
        )}

        <div className="bg-dark-800/30 rounded-2xl border border-dark-700/30 p-4">
          <p className="text-[10px] text-gray-500 flex items-start gap-1.5">
            <Activity className="w-3 h-3 flex-shrink-0 mt-0.5" />
            Worker evaluates each session every 5 minutes against the latest candle. Trades fire at the candle's close price + ~10 bps slippage + 40 bps fee — realistic retail execution. No orders go to real exchanges. State persists if you close the browser; sessions keep running on the server.
          </p>
        </div>

        {showCreate && (
          <CreateSessionModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); setToast({ type: 'success', message: 'Session started' }); load(); }}
            savedStrategies={savedStrategies}
            connectedExchanges={connectedExchanges}
          />
        )}

        {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
      </div>
    </FeatureGate>
  );
}

// ─── Session Card ────────────────────────────────────────────────────────────

function SessionCard({ s, onAction }) {
  const st = STATUS_STYLE[s.status] || STATUS_STYLE.stopped;
  const isProfit = (s.totalReturnPct ?? 0) >= 0;
  const ageMin = s.startedAt ? Math.floor((Date.now() - new Date(s.startedAt).getTime()) / 60000) : 0;

  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-white font-bold truncate">{s.strategyName || 'Strategy'}</h3>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
          </div>
          <p className="text-xs text-gray-500 font-mono">
            {s.exchange} · {s.symbol} · {s.timeframe} · running {ageMin}m
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
        <div className="bg-dark-700/40 rounded-lg p-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Equity</p>
          <p className={`font-bold font-mono mt-0.5 ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
            {fmtUSD(s.currentEquity)}
          </p>
          <p className="text-[9px] text-gray-500">vs {fmtUSD(s.initialCapital)}</p>
        </div>
        <div className="bg-dark-700/40 rounded-lg p-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Return</p>
          <p className={`font-bold font-mono mt-0.5 ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
            {fmtPct(s.totalReturnPct)}
          </p>
        </div>
        <div className="bg-dark-700/40 rounded-lg p-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Trades</p>
          <p className="font-bold text-white font-mono mt-0.5">{s.totalTrades ?? 0}</p>
          <p className="text-[9px] text-gray-500">{s.winRate ? `${(s.winRate * 100).toFixed(0)}% win` : '—'}</p>
        </div>
        <div className="bg-dark-700/40 rounded-lg p-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Last price</p>
          <p className="font-bold text-white font-mono mt-0.5">{s.currentPrice ? fmtUSD(s.currentPrice, 2) : '—'}</p>
          {s.hasOpenPosition && <p className="text-[9px] text-emerald-400">📈 Open</p>}
        </div>
      </div>

      <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-3">
        Last tick: {s.lastTickAt ? new Date(s.lastTickAt).toLocaleTimeString() : 'pending'}
        <span className="text-gray-600">·</span>
        Worker runs every 5 min
      </div>

      <div className="flex items-center gap-2">
        {s.status === 'running' && (
          <button onClick={() => onAction(pausePaperSession, s.sessionId, 'Session paused')}
            className="flex-1 px-3 py-1.5 rounded-lg text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 flex items-center justify-center gap-1">
            <Pause className="w-3 h-3" /> Pause
          </button>
        )}
        {s.status === 'paused' && (
          <button onClick={() => onAction(resumePaperSession, s.sessionId, 'Session resumed')}
            className="flex-1 px-3 py-1.5 rounded-lg text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 flex items-center justify-center gap-1">
            <Play className="w-3 h-3" /> Resume
          </button>
        )}
        {s.status !== 'stopped' && (
          <button onClick={() => onAction(stopPaperSession, s.sessionId, 'Session stopped')}
            className="flex-1 px-3 py-1.5 rounded-lg text-xs bg-gray-500/10 text-gray-400 border border-gray-500/20 hover:bg-gray-500/20 flex items-center justify-center gap-1">
            <Square className="w-3 h-3" /> Stop
          </button>
        )}
        <button onClick={() => { if (window.confirm('Delete this session? Trade history will be lost.')) onAction(deletePaperSession, s.sessionId, 'Session deleted'); }}
          className="px-3 py-1.5 rounded-lg text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ─── Create Session Modal ───────────────────────────────────────────────────

function CreateSessionModal({ onClose, onCreated, savedStrategies, connectedExchanges }) {
  const [strategyId, setStrategyId] = useState(savedStrategies[0]?.strategyId || '');
  const [exchange, setExchange] = useState(connectedExchanges[0]?.exchangeId || '');
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [initialCapital, setInitialCapital] = useState(10000);
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!exchange) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(exchange)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || res?.data?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(symbol)) setSymbol(sorted[0]);
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange]);

  const filteredPairs = useMemo(() => {
    return pairSearch
      ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 200)
      : pairs.slice(0, 200);
  }, [pairs, pairSearch]);

  async function handleCreate() {
    setError(null);
    setCreating(true);
    try {
      await createPaperSession({
        strategyId,
        exchange,
        symbol,
        timeframe: '1h',
        initialCapital: parseFloat(initialCapital) || 10000,
      });
      onCreated();
    } catch (err) {
      setError(err.message || 'Create failed');
    }
    setCreating(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold">New Paper Trading Session</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider flex items-center gap-1">
              Strategy
              <InfoTip side="right">Pick a strategy you built in Strategy Builder. The same strategyId can later be assigned to a live bot — paper and live use the same engine.</InfoTip>
            </label>
            <select value={strategyId} onChange={e => setStrategyId(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm">
              {savedStrategies.map(s => (
                <option key={s.strategyId} value={s.strategyId}>{s.name}{s.category ? ` (${s.category})` : ''}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">Exchange</label>
              <select value={exchange} onChange={e => setExchange(e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm">
                {connectedExchanges.map(ex => (
                  <option key={ex.exchangeId} value={ex.exchangeId}>{ex.name || ex.exchangeId}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 mb-1 block uppercase tracking-wider">Timeframe</label>
              <input value="1h" disabled className="w-full bg-dark-700/50 border border-dark-600 rounded-lg p-2 text-gray-400 text-sm font-mono" />
              <p className="text-[9px] text-gray-500 mt-1">v1: 1h only · 5m/15m/4h in v1.1</p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider">Pair</label>
              <span className="text-[10px] text-gray-500">{pairsLoading ? 'Loading…' : `${pairs.length} on ${exchange}`}</span>
            </div>
            <div className="relative mb-1">
              <Search className="w-3 h-3 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={pairSearch}
                onChange={e => setPairSearch(e.target.value)}
                placeholder="Search pair (e.g. ETH/USDC)"
                className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-6 pr-2 py-1.5 text-white text-xs font-mono" />
            </div>
            <div className="max-h-32 overflow-y-auto bg-dark-700/40 border border-dark-600 rounded-lg">
              {filteredPairs.length === 0 ? (
                <div className="p-2 text-[10px] text-gray-500">No pairs match "{pairSearch}"</div>
              ) : (
                filteredPairs.map(p => (
                  <button key={p} type="button" onClick={() => setSymbol(p)}
                    className={`block w-full text-left px-2 py-1 text-[11px] font-mono transition-colors ${
                      symbol === p ? 'bg-amber-500/15 text-amber-300' : 'text-gray-300 hover:bg-dark-600'
                    }`}>
                    {p}
                  </button>
                ))
              )}
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Selected: <span className="font-mono text-white">{symbol}</span></p>
          </div>

          <div>
            <label className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider flex items-center gap-1">
              Virtual capital (USD)
              <InfoTip side="right">Pick whatever you'd actually trade with — % return scales the same regardless. Your real exchange balance is never touched.</InfoTip>
            </label>
            <input type="number" value={initialCapital} onChange={e => setInitialCapital(e.target.value)} min={100} step={100}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm font-mono" />
            <p className="text-[10px] text-gray-500 mt-1">Used for position sizing — no real money</p>
          </div>

          {error && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-2.5 text-red-400 text-xs flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          <button onClick={handleCreate} disabled={creating || !strategyId || !exchange || !symbol}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-amber-500 text-dark-900 hover:bg-amber-400 disabled:opacity-40 flex items-center justify-center gap-2">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Start Session
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 5000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium border
      ${type === 'success' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
        : 'bg-red-500/15 text-red-300 border-red-500/30'}`}>
      {message}
    </div>
  );
}
